import { useMemo, useState } from "react";
import { useEntity } from "../hooks/useEntity";
import { collaborationEntityConfig } from "../entities/Collaboration";
import { collaborationMessageEntityConfig } from "../entities/CollaborationMessage";
import { Users, UserPlus, Trash2, Mail, Send, MessageCircle } from "lucide-react";

interface CollaborationPanelProps {
  documentId: number;
  currentUserId: number;
  collaborators?: Collaboration[];
  onInviteCollaborator?: (invite: CollaborationInvitePayload) => void;
  onRemoveCollaborator?: (id: number) => void;
  messages?: CollaborationMessage[];
  onSendMessage?: (message: CollaborationMessageInput) => void;
  documentTitle?: string;
  currentUserName?: string;
}

type Collaboration = {
  id: number;
  documentId: number;
  userId: number;
  email?: string;
  displayName?: string;
  role: "owner" | "editor" | "commenter" | "viewer";
  invitedBy: number;
  status: "pending" | "accepted" | "declined";
  lastAccessedAt: string;
  created_at: string;
  updated_at: string;
};

type CollaborationInvitePayload = {
  email: string;
  role: "editor" | "commenter" | "viewer";
};

type CollaborationMessage = {
  id?: string | number;
  documentId: number;
  userId: number;
  body: string;
  sentAt?: string;
  created_at?: string;
  updated_at?: string;
};

type CollaborationMessageInput = {
  body: string;
};

export default function CollaborationPanel({
  documentId,
  currentUserId,
  collaborators,
  onInviteCollaborator,
  onRemoveCollaborator,
  messages,
  onSendMessage,
  documentTitle,
  currentUserName,
}: CollaborationPanelProps) {
  const {
    items: entityCollaborations,
    create: createCollaboration,
    remove: removeCollaboration,
  } = useEntity<Collaboration>(collaborationEntityConfig);
  const { items: entityMessages, create: createCollaborationMessage } = useEntity<CollaborationMessage>(
    collaborationMessageEntityConfig
  );
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "commenter" | "viewer">("editor");
  const [chatDraft, setChatDraft] = useState("");
  const [localMessages, setLocalMessages] = useState<CollaborationMessage[]>([]);

  const usingControlled = Array.isArray(collaborators) && !!onInviteCollaborator;
  const collaboratorList = (usingControlled ? collaborators! : entityCollaborations).filter(
    (c) => c.documentId === documentId
  );
  const usingMessageController = Array.isArray(messages) && !!onSendMessage;
  const persistedMessages = useMemo(() => {
    const pool = usingMessageController ? messages ?? [] : entityMessages;
    return pool.filter((msg) => msg.documentId === documentId);
  }, [usingMessageController, messages, entityMessages, documentId]);

  const conversation = persistedMessages.length ? persistedMessages : localMessages;

  const handleInvite = async () => {
    if (!inviteEmail) return;

    if (usingControlled) {
      onInviteCollaborator?.({ email: inviteEmail, role: inviteRole });
    } else {
      const normalizedEmail = inviteEmail.trim();
      await createCollaboration({
        documentId,
        userId: Math.floor(Math.random() * 10000), // Simulated user ID
        email: normalizedEmail,
        displayName: normalizedEmail.split("@")[0] || normalizedEmail,
        role: inviteRole,
        invitedBy: currentUserId,
        status: "pending",
        lastAccessedAt: new Date().toISOString(),
      });

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (supabaseUrl && supabaseAnon) {
        const inviteLink = `${window.location.origin}/organizer?documentId=${documentId}`;
        try {
          await fetch(`${supabaseUrl}/functions/v1/send-invite`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${supabaseAnon}`,
              apikey: supabaseAnon,
            },
            body: JSON.stringify({
              to: normalizedEmail,
              documentId,
              inviterName: currentUserName || "A classmate",
              inviteLink,
              product: "thesiscribe",
            }),
          });
        } catch (err) {
          console.error("Failed to send invite email", err);
        }
      }
    }

    setInviteEmail("");
    setShowInviteForm(false);
  };

  const handleRemove = async (id: number) => {
    if (usingControlled) {
      onRemoveCollaborator?.(id);
    } else {
      await removeCollaboration(id);
    }
  };

  const handleSendMessage = async () => {
    if (!chatDraft.trim()) return;
    const trimmed = chatDraft.trim();
    const fallbackMessage: CollaborationMessage = {
      id: `${documentId}-${Date.now()}`,
      documentId,
      userId: currentUserId,
      body: trimmed,
      sentAt: new Date().toISOString(),
    };

    if (onSendMessage) {
      onSendMessage({ body: trimmed });
    } else {
      try {
        await createCollaborationMessage({
          documentId,
          userId: currentUserId,
          body: trimmed,
          sentAt: fallbackMessage.sentAt,
        });
      } catch (err) {
        setLocalMessages((prev) => [fallbackMessage, ...prev.slice(0, 49)]);
      }
    }
    setChatDraft("");
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "owner": return "bg-purple-500/20 border-purple-400/40 text-purple-300";
      case "editor": return "bg-blue-500/20 border-blue-400/40 text-blue-300";
      case "commenter": return "bg-yellow-500/20 border-yellow-400/40 text-yellow-300";
      case "viewer": return "bg-gray-500/20 border-gray-400/40 text-gray-300";
      default: return "bg-white/10 border-white/20 text-white";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "accepted": return "bg-green-500/20 border-green-400/40 text-green-300";
      case "pending": return "bg-yellow-500/20 border-yellow-400/40 text-yellow-300";
      case "declined": return "bg-red-500/20 border-red-400/40 text-red-300";
      default: return "bg-white/10 border-white/20 text-white";
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-white/90" />
          <h3 className="text-lg font-semibold text-white">Collaborators</h3>
        </div>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="p-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white transition-all duration-200"
        >
          <UserPlus className="w-4 h-4" />
        </button>
      </div>

      {showInviteForm && (
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 mb-4 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-white/70" />
            <span className="text-sm text-white/70">Invite Collaborator</span>
          </div>

          <input
            type="email"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 mb-2 focus:outline-none focus:border-white/40"
          />

          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as any)}
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white mb-3 focus:outline-none focus:border-white/40"
          >
            <option value="editor">Editor - Can edit content</option>
            <option value="commenter">Commenter - Can add comments</option>
            <option value="viewer">Viewer - Can only view</option>
          </select>

          <button
            onClick={handleInvite}
            className="w-full px-4 py-2 bg-white/15 hover:bg-white/25 border border-white/30 rounded-lg text-white font-medium transition-all duration-200"
          >
            Send Invitation
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-3">
        {collaboratorList.map((collab) => (
          <div
            key={collab.id}
            className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg hover:bg-white/15 transition-all duration-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    U{collab.userId}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white/90 font-medium">
                      {collab.displayName || collab.email || `User #${collab.userId}`}
                    </p>
                    {collab.email && (
                      <p className="text-[11px] text-white/60">{collab.email}</p>
                    )}
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${getRoleBadgeColor(collab.role)}`}>
                        {collab.role}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeColor(collab.status)}`}>
                        {collab.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {collab.role !== "owner" && (
                <button
                  onClick={() => handleRemove(collab.id)}
                  className="p-1.5 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-all duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}

        {collaboratorList.length === 0 && !showInviteForm && (
          <div className="text-center py-8 text-white/50">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No collaborators yet</p>
            <p className="text-xs mt-1">Click + to invite others</p>
          </div>
        )}
      </div>

      <div className="mt-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 shadow-lg flex flex-col h-64">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4 text-white/70" />
          <div>
            <p className="text-sm text-white/80 font-semibold">Document Chat</p>
            <p className="text-[11px] text-white/50">{documentTitle || "Shared Draft"}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {conversation.length === 0 ? (
            <p className="text-xs text-white/50 text-center py-6">No messages yet. Say hello!</p>
          ) : (
            conversation.map((msg) => (
              <div
                key={msg.id ?? `${msg.documentId}-${msg.userId}-${msg.sentAt}`}
                className={`p-2 rounded-xl border text-xs ${
                  msg.userId === currentUserId
                    ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-50"
                    : "bg-white/5 border-white/10 text-white/80"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">
                    {msg.userId === currentUserId ? currentUserName || "You" : `User #${msg.userId}`}
                  </span>
                  <span className="text-[10px] text-white/50">
                    {(msg.sentAt || msg.created_at)
                      ? new Date(msg.sentAt || msg.created_at || "").toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "--:--"}
                  </span>
                </div>
                <p>{msg.body}</p>
              </div>
            ))
          )}
        </div>
        <div className="mt-2 flex gap-2">
          <input
            value={chatDraft}
            onChange={(e) => setChatDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Message collaborators"
            className="flex-1 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 text-xs"
          />
          <button
            onClick={handleSendMessage}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white text-xs flex items-center gap-1"
          >
            <Send className="w-3 h-3" />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
